import { useEffect, useState } from 'react';
import type { ProductCategory } from '@/domain/types';
import { deleteCategory, listCategories, saveCategory, type TaxonomyInput } from '@/services/catalog';

const emptyInput: TaxonomyInput = { name: '', slug: '', description: '', active: true, sortOrder: 0 };

function slugify(value: string) {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function AdminCategories() {
  const [items, setItems] = useState<ProductCategory[]>([]);
  const [editing, setEditing] = useState<ProductCategory | null>(null);
  const [input, setInput] = useState<TaxonomyInput>(emptyInput);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      setItems(await listCategories());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load categories');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  const startEdit = (item: ProductCategory) => {
    setEditing(item);
    setInput({
      name: item.name,
      slug: item.slug,
      description: item.description,
      active: item.active,
      sortOrder: item.sortOrder,
    });
  };

  const reset = () => {
    setEditing(null);
    setInput(emptyInput);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await saveCategory(input, editing?.id);
      reset();
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this category? Products using it will keep working without a category.')) return;
    try {
      await deleteCategory(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete category');
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-surface-900">Categories</h1>
        <p className="text-sm text-surface-500 mt-1">Create product categories such as WordPress or Shopify.</p>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-surface-200 p-5 grid grid-cols-1 md:grid-cols-5 gap-3">
        <input value={input.name} onChange={(e) => setInput({ ...input, name: e.target.value, slug: input.slug || slugify(e.target.value) })} placeholder="Name" className="px-3 py-2.5 rounded-lg border border-surface-300 text-sm" />
        <input value={input.slug} onChange={(e) => setInput({ ...input, slug: slugify(e.target.value) })} placeholder="slug" className="px-3 py-2.5 rounded-lg border border-surface-300 text-sm" />
        <input value={input.description} onChange={(e) => setInput({ ...input, description: e.target.value })} placeholder="Description" className="md:col-span-2 px-3 py-2.5 rounded-lg border border-surface-300 text-sm" />
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-surface-600">
            <input type="checkbox" checked={input.active} onChange={(e) => setInput({ ...input, active: e.target.checked })} className="w-4 h-4 accent-brand-600" />
            Active
          </label>
          <button disabled={saving || !input.name || !input.slug} className="ml-auto px-4 py-2 rounded-lg bg-brand-600 disabled:bg-surface-300 text-white text-sm font-semibold">
            {editing ? 'Update' : 'Add'}
          </button>
          {editing && <button type="button" onClick={reset} className="px-3 py-2 rounded-lg border border-surface-300 text-sm">Cancel</button>}
        </div>
      </form>

      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-surface-500">Loading categories...</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-surface-100 last:border-b-0">
                  <td className="px-5 py-3 font-semibold text-surface-900">{item.name}</td>
                  <td className="px-5 py-3 text-surface-500">{item.slug}</td>
                  <td className="px-5 py-3 text-surface-500">{item.active ? 'Active' : 'Inactive'}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => startEdit(item)} className="px-3 py-1.5 rounded-lg hover:bg-surface-100 text-surface-600">Edit</button>
                    <button onClick={() => handleDelete(item.id)} className="px-3 py-1.5 rounded-lg hover:bg-red-50 text-red-600">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
